import React, { useState } from "react";
import { NavLink, Outlet, Link, useLocation } from "react-router-dom";
import DigitalSetuLogo from "@/components/DigitalSetuLogo";
import { BRAND, waLink, fullAddress, isOpenNow } from "@/lib/brand";
import { List, X, WhatsappLogo, Phone, ArrowUpRight, MapPin, EnvelopeSimple } from "@phosphor-icons/react";
import { Toaster } from "sonner";

const NAV = [
  { to: "/",          label: "Home" },
  { to: "/services",  label: "Services" },
  { to: "/apply",     label: "Apply Online" },
  { to: "/track",     label: "Track" },
  { to: "/pricing",   label: "Pricing" },
  { to: "/branches",  label: "Branches" },
  { to: "/about",     label: "About" },
  { to: "/contact",   label: "Contact" },
];

export default function PublicLayout() {
  const [open, setOpen] = useState(false);
  const loc = useLocation();

  return (
    <div className="min-h-screen flex flex-col bg-white text-slate-900" data-testid="public-layout">
      {/* Top announcement strip */}
      <div className="bg-[#1e3a8a] text-white text-xs">
        <div className="max-w-7xl mx-auto px-4 h-8 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="hidden sm:inline">🇮🇳 Empowering India's Digital Service Centres</span>
            <a href={waLink("Hi DigitalSetu, I need help with a service.")} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 hover:text-orange-300" data-testid="topbar-whatsapp">
              <WhatsappLogo size={12} /> WhatsApp {BRAND.whatsapp}
            </a>
          </div>
          <Link to="/login" className="inline-flex items-center gap-1 hover:text-orange-300" data-testid="topbar-staff-login">
            Staff Login <ArrowUpRight size={12} />
          </Link>
        </div>
      </div>

      {/* Header */}
      <header className="sticky top-0 z-30 bg-white/95 backdrop-blur border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5" data-testid="brand-logo">
            <DigitalSetuLogo size={38} />
            <div>
              <div className="font-bold text-lg leading-tight" style={{ fontFamily: "var(--font-heading)", color: "#1e3a8a" }}>{BRAND.name}</div>
              <div className="text-[9px] uppercase tracking-[0.2em] text-slate-500 -mt-0.5">{BRAND.sub}</div>
            </div>
          </Link>

          <nav className="hidden lg:flex items-center gap-1" data-testid="public-nav">
            {NAV.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.to === "/"}
                data-testid={`nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={({ isActive }) =>
                  `px-3.5 py-2 rounded-md text-sm font-medium transition-colors ${isActive ? "text-[#1e3a8a] bg-blue-50" : "text-slate-600 hover:text-[#1e3a8a] hover:bg-slate-50"}`
                }
              >
                {n.label}
              </NavLink>
            ))}
          </nav>

          <div className="hidden md:flex items-center gap-2">
            <Link to="/apply" className="hidden sm:inline-flex items-center gap-1.5 px-4 h-10 rounded-full text-sm font-semibold text-white bg-[#ea580c] hover:bg-orange-700 transition-colors shadow-sm" data-testid="cta-apply-header">
              Apply Now
            </Link>
          </div>

          <button className="lg:hidden p-2 rounded-md hover:bg-slate-100" onClick={() => setOpen(!open)} data-testid="mobile-menu-toggle" aria-label="Menu">
            {open ? <X size={22} /> : <List size={22} />}
          </button>
        </div>

        {/* Mobile menu */}
        {open && (
          <div className="lg:hidden border-t border-slate-200 bg-white" data-testid="mobile-nav">
            <div className="p-3 space-y-1">
              {NAV.map((n) => (
                <NavLink
                  key={n.to}
                  to={n.to}
                  end={n.to === "/"}
                  onClick={() => setOpen(false)}
                  className={({ isActive }) =>
                    `block px-4 py-2.5 rounded-md text-sm font-medium ${isActive ? "text-[#1e3a8a] bg-blue-50" : "text-slate-700 hover:bg-slate-50"}`
                  }
                >
                  {n.label}
                </NavLink>
              ))}
              <Link to="/apply" onClick={() => setOpen(false)} className="block mt-2 px-4 py-2.5 rounded-md text-sm font-semibold text-center text-white bg-[#ea580c]">
                Apply Now
              </Link>
            </div>
          </div>
        )}
      </header>

      {/* Body */}
      <main className="flex-1">
        <Outlet />
      </main>

      {/* Footer */}
      <footer className="bg-slate-900 text-slate-300 mt-20" data-testid="public-footer">
        <div className="max-w-7xl mx-auto px-4 py-14 grid grid-cols-1 md:grid-cols-4 gap-10">
          {/* Brand + Address */}
          <div className="md:col-span-2">
            <div className="flex items-center gap-2 mb-4">
              <DigitalSetuLogo size={38} variant="mono" />
              <div>
                <div className="flex items-center gap-2">
                  <div className="font-bold text-lg text-white leading-tight" style={{ fontFamily: "var(--font-heading)" }}>{BRAND.legalName}</div>
                  <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-emerald-500/20 border border-emerald-400/40 text-emerald-200 text-[8px] uppercase tracking-widest font-bold">Head Office</span>
                </div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-orange-300 -mt-0.5">{BRAND.authorization}</div>
              </div>
            </div>
            <div className="text-sm leading-relaxed space-y-2" data-testid="footer-address">
              <div className="flex items-start gap-2">
                <MapPin size={14} className="text-orange-300 shrink-0 mt-0.5" weight="fill" />
                <address className="not-italic">
                  {BRAND.address.line1},<br />
                  {BRAND.address.line2},<br />
                  {BRAND.address.line3},<br />
                  {BRAND.address.city} – {BRAND.address.pincode}
                </address>
              </div>
              <a href={waLink("Hi Priyanka Enterprises, I need help with a service.")} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 hover:text-white transition-colors" data-testid="footer-whatsapp">
                <WhatsappLogo size={14} className="text-emerald-400" weight="fill" /> {BRAND.whatsapp}
              </a>
              <div className="flex items-center gap-2 text-xs text-slate-400">
                <span className={`inline-block w-2 h-2 rounded-full ${isOpenNow() ? "bg-emerald-400" : "bg-slate-500"}`} />
                {isOpenNow() ? "Open now" : "Closed"} · {BRAND.hours}
              </div>
              <a href={BRAND.mapUrl} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-orange-300 hover:text-white text-xs mt-1" data-testid="footer-map">
                <MapPin size={12} weight="fill" /> Get Directions
              </a>
            </div>
          </div>

          <div>
            <div className="text-xs font-semibold uppercase tracking-widest text-white mb-3">Services</div>
            <ul className="space-y-1.5 text-sm">
              <li><Link to="/services" className="hover:text-white">PAN Card</Link></li>
              <li><Link to="/services" className="hover:text-white">Aadhaar</Link></li>
              <li><Link to="/services" className="hover:text-white">Passport</Link></li>
              <li><Link to="/services" className="hover:text-white">GST · UDYAM</Link></li>
              <li><Link to="/services" className="hover:text-white">Rent Agreement</Link></li>
            </ul>
          </div>

          <div>
            <div className="text-xs font-semibold uppercase tracking-widest text-white mb-3">Platform</div>
            <ul className="space-y-1.5 text-sm">
              <li><Link to="/about"    className="hover:text-white">About Us</Link></li>
              <li><Link to="/pricing"  className="hover:text-white">Pricing</Link></li>
              <li><Link to="/branches" className="hover:text-white">Branches</Link></li>
              <li><Link to="/contact"  className="hover:text-white">Contact</Link></li>
              <li><Link to="/track"    className="hover:text-white">Track Application</Link></li>
              <li><Link to="/login"    className="hover:text-white">Staff Login</Link></li>
            </ul>
          </div>
        </div>

        <div className="border-t border-slate-800">
          <div className="max-w-7xl mx-auto px-4 py-4 flex flex-wrap items-center justify-between gap-3 text-xs text-slate-500">
            <div>© {new Date().getFullYear()} {BRAND.name}. All Rights Reserved.</div>
            <div className="flex items-center gap-3">
              <a href={waLink()} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 hover:text-white">
                <WhatsappLogo size={12} weight="fill" /> WhatsApp
              </a>
              <span>Made in India · A product of {BRAND.legalName}</span>
            </div>
          </div>
        </div>
      </footer>
      <Toaster position="top-right" richColors />
    </div>
  );
}
